// CheerpX engine: x86 Linux (Leaning Technologies' WebVM images) running in this page. Needs
// crossOriginIsolated. Re:Vault sends the isolation headers everywhere except the ad-capable
// start page on approved sites (docs/ADS-PLUMBING.md 1.2), where the Linux page offers its own
// window instead. Ported from EndisOS src/linux/cheerpx/adapter.ts (itself adapted from WebVM,
// Apache-2.0): device stack, console bridge, KMS canvas, activity callbacks, run loop, Tailscale.
import { LinuxError, listeners, sleep } from "../engine.js";
import { acquireMachineLock } from "../lock.js";
import { readJSON, writeJSON, remove } from "../../shared/store.js";
import { RESET_KEY } from "../../shared/linux-store.js";
import { createMountTable, deleteMachineDisks } from "./devices.js";
import { EXPORT_HELPER, EXPORT_HELPER_SCRIPT, OUTBOX_LIST, exportIntro, isSafeExportName, parseOutboxList, safeImportName } from "./export-helper.js";
import { checkPreconditions, loadRuntime, mapRuntimeError, readPreconditionEnv } from "./loader.js";
import { awaitOperation } from "./operation.js";

export const CAPABILITIES = Object.freeze({ id: "cheerpx", name: "CheerpX", networking: "tailscale", graphics: true, resizableConsole: false });
const TAILSCALE_RUNNING = 6; // IPN state reported by stateUpdateCb (WebVM maps only 6 = Running)
const MAX_IMPORT_BYTES = 64 * 1024 * 1024;
const LATENCY_WINDOW = 30;

export class CheerpxEngine {
	// opts: { runtimeUrl, accepted, pageUrl?, display?: { canvas, onActivate(vt) }, importer?,
	//   loadTimeoutMs?, startupTimeoutMs?, flushTimeoutMs?, settleMs?, env?, acquireLease? }
	constructor(opts) {
		this.opts = opts;
		this.capabilities = CAPABILITIES;
		this.stateValue = "idle";
		this.errorValue = null;
		this.states = listeners();
		this.activities = listeners();
		this.networks = listeners();
		this.activity = { cpu: "idle", disk: "idle", diskLatencyMs: null, processes: 0 };
		this.latencies = [];
		this.table = null;
		this.cx = null;
		this.send = null;
		this.stopped = false;
		this.release = null;
		this.flushFlight = null;
		this.activateVt = null;
		this.starting = null;
		this.networkState = "disconnected";
		this.networkIp = null;
		this.networkLoginUrl = null;
		const self = this;
		this.network = {
			login: () => self.loginToNetwork(),
			get state() { return self.networkState; },
			get ip() { return self.networkIp; },
			get loginUrl() { return self.networkLoginUrl; },
		};
	}

	get state() { return this.stateValue; }
	get error() { return this.errorValue; }
	get exportHelperPath() { return EXPORT_HELPER; }
	onState(fn) { return this.states.add(fn); }
	onActivity(fn) { return this.activities.add(fn); }
	onNetwork(fn) { return this.networks.add(fn); }

	async start(machine, console, signal) {
		if (this.starting || this.cx || !["idle", "stopped", "error"].includes(this.stateValue)) throw new LinuxError("PROCESS_FAILED", "The machine is already running.");
		machine = structuredClone(machine);
		this.stopped = false;
		this.errorValue = null;
		const controller = new AbortController();
		this.starting = controller;
		const cancel = () => controller.abort(new LinuxError("ABORTED", "The Linux tab was closed while starting."));
		if (signal.aborted) cancel(); else signal.addEventListener("abort", cancel, { once: true });
		const startup = controller.signal;
		// Bounds the WHOLE start (import, every device, Linux.create) - a promise race alone would
		// lose ownership of a late runtime, so late results are reclaimed in awaitOperation.
		const deadline = setTimeout(() => controller.abort(new LinuxError(
			this.stateValue === "opening-disk" ? "IMAGE_UNREACHABLE" : "RUNTIME_INIT_FAILED",
			"Linux took too long to start. Check your connection, then press Start to retry."
		)), this.opts.startupTimeoutMs || 120000);
		try {
			startup.throwIfAborted();
			this.setState("checking");
			const pre = checkPreconditions(readPreconditionEnv(this.opts.accepted, this.opts.env));
			if (pre) throw new LinuxError(pre.code, pre.message);
			this.release = await (this.opts.acquireLease || acquireMachineLock)(machine.id, startup);
			this.setState("loading-runtime");
			const runtime = await loadRuntime(this.opts.runtimeUrl, { signal: startup, timeoutMs: this.opts.loadTimeoutMs || 30000, importer: this.opts.importer });
			startup.throwIfAborted();
			this.setState("opening-disk");
			const table = await createMountTable(runtime, machine, { pageUrl: this.opts.pageUrl || location.href, signal: startup });
			startup.throwIfAborted();
			this.table = table;
			await awaitOperation(this.writeExportHelper(table), startup);
			this.setState("booting");
			let cx;
			try {
				cx = await awaitOperation(runtime.Linux.create({ mounts: table.mounts, networkInterface: this.networkInterface() }), startup, (late) => { try { late.delete(); } catch (e) {} });
			} catch (err) {
				const m = mapRuntimeError(err);
				throw new LinuxError(m.code === "RUNTIME_LOAD_FAILED" ? "RUNTIME_INIT_FAILED" : m.code, m.message, err);
			}
			this.cx = cx;
			startup.throwIfAborted();
			// The console size is FIXED by this call (no resize API): the page fits the terminal first.
			this.send = cx.setCustomConsole((buf, vt) => { if (vt === 1) console.write(buf); }, console.cols, console.rows);
			if (this.opts.display) {
				cx.setKmsCanvas(this.opts.display.canvas, 1024, 768);
				this.activateVt = cx.setActivateConsole((vt) => this.opts.display.onActivate(vt));
			}
			cx.registerCallback("cpuActivity", (s) => this.updateActivity({ cpu: s === "wait" ? "busy" : "idle" }));
			cx.registerCallback("diskActivity", (s) => this.updateActivity({ disk: s === "wait" ? "busy" : "idle" }));
			cx.registerCallback("diskLatency", (v) => { if (typeof v === "number") this.pushLatency(v); });
			cx.registerCallback("processCreated", () => this.updateActivity({ processes: this.activity.processes + 1 }));
			if (!machine.desktop) console.write(new TextEncoder().encode(exportIntro(machine.name)));
			this.runLoop(cx, machine);
			this.setState("running");
		} catch (err) {
			await this.stop().catch(() => {});
			const m = mapRuntimeError(err);
			this.errorValue = m;
			this.setState("error");
			throw err instanceof LinuxError ? err : new LinuxError(m.code, m.message, err);
		} finally {
			clearTimeout(deadline);
			signal.removeEventListener("abort", cancel);
			this.starting = null;
		}
	}

	sendInput(bytes) {
		const send = this.send;
		if (!send || this.stateValue !== "running" || this.flushFlight) return;
		for (const b of bytes) send(b);
	}
	resize() {} // no console resize API in CheerpX
	activateConsole(vt) { if (this.activateVt) this.activateVt(vt); if (this.opts.display) this.opts.display.onActivate(vt); }

	// "Save disk". In pinned 1.3.9 the guest sync AND flushIO() can resolve before the last worker
	// block writes reach IndexedDB (an immediate reload lost files in EndisOS's live test). So:
	// guest /bin/sync, let the writeback batch land (~1 s), then flushIO(). A timing workaround,
	// not a crash-consistent snapshot; a timeout is reported as UNCONFIRMED, never as success.
	async flush() {
		if (this.flushFlight) return this.flushFlight;
		const cx = this.cx;
		if (!cx || this.stateValue !== "running") throw new LinuxError("STORAGE_FAILED", "Start the machine before saving its disk.");
		if (typeof cx.flushIO !== "function") throw new LinuxError("STORAGE_FAILED", "This CheerpX runtime has no disk-flush API.");
		const controller = new AbortController();
		const deadline = setTimeout(() => controller.abort(new LinuxError("STORAGE_FAILED", "Saving the disk timed out and is NOT confirmed. Keep this tab open and download important files before reloading.")), this.opts.flushTimeoutMs || 30000);
		const checkpoint = async () => {
			const result = await awaitOperation(cx.run("/bin/sync", [], { cwd: "/", uid: 0, gid: 0 }), controller.signal);
			if (result && result.status !== 0) throw new LinuxError("STORAGE_FAILED", "The guest's sync failed (status " + result.status + ").");
			await sleep(this.opts.settleMs === undefined ? 1000 : this.opts.settleMs);
			if (this.cx !== cx || this.stopped) throw new LinuxError("ABORTED", "The machine stopped before saving finished.");
			await awaitOperation(cx.flushIO(), controller.signal);
		};
		this.flushFlight = checkpoint();
		try { await this.flushFlight; } finally { clearTimeout(deadline); this.flushFlight = null; }
	}

	async stop() {
		if (this.starting) this.starting.abort(new LinuxError("ABORTED", "Linux startup was cancelled."));
		if (this.stopped && !this.cx) { this.releaseLock(); return; }
		this.stopped = true;
		this.setState("stopping");
		try { if (this.cx) this.cx.delete(); }
		catch (err) { this.stopped = false; throw new LinuxError("PROCESS_FAILED", "CheerpX couldn't stop. Close this tab before resetting its disk.", err); }
		const hadRuntime = !!this.cx;
		this.cx = null; this.send = null; this.activateVt = null; this.table = null;
		// delete() does not stop the runtime's workers or its queued IndexedDB writes (see PITFALLS),
		// so once a runtime existed the lock is kept until this document goes away (browsers release
		// Web Locks on unload). Another tab can't open the same disk while stale writers may be alive.
		if (!hadRuntime) this.releaseLock();
		this.setState("stopped");
	}
	async dispose() { try { await this.stop(); } catch (e) {} this.releaseLock(); this.states.clear(); this.activities.clear(); this.networks.clear(); }
	releaseLock() { if (this.release) { try { this.release(); } catch (e) {} this.release = null; } }

	// In-place reset is unsafe: delete() leaves vendor DB handles and queued writes alive, which can
	// resurrect the disk. Reset = marker + reload + delete before CheerpX is imported (see below).
	async reset() { throw new LinuxError("STORAGE_FAILED", "Reset reloads this tab first; use Reset disk in the Machine tab."); }

	async importFile(name, blob) {
		const table = this.table;
		if (!table) throw new LinuxError("PROCESS_FAILED", "Start the machine before sending files to it.");
		if (blob.size > MAX_IMPORT_BYTES) throw new LinuxError("STORAGE_FAILED", "Files sent to CheerpX machines must be 64 MB or smaller (" + Math.round(blob.size / 1048576) + " MB).");
		const safe = safeImportName(name);
		try { await table.data.writeFile("/" + safe, new Uint8Array(await blob.arrayBuffer())); }
		catch (err) { throw new LinuxError("STORAGE_FAILED", "The file couldn't be sent to Linux (" + (err && err.message ? err.message : String(err)) + ").", err); }
		return "/data/" + safe;
	}
	async exportFile(name) {
		const table = this.table;
		if (!table) throw new LinuxError("PROCESS_FAILED", "Start the machine before downloading its files.");
		const base = String(name).slice(String(name).lastIndexOf("/") + 1);
		if (!isSafeExportName(base)) return null;
		return table.files.readFileAsBlob("/outbox/" + base);
	}
	async listExports() {
		const table = this.table;
		if (!table) return [];
		const list = await table.files.readFileAsBlob(OUTBOX_LIST);
		return list ? parseOutboxList(await list.text()) : [];
	}

	// --- internals -----------------------------------------------------------

	setState(s) { if (this.stateValue !== s) { this.stateValue = s; this.states.emit(s); } }
	updateActivity(patch) { this.activity = { ...this.activity, ...patch }; this.activities.emit(this.activity); }
	pushLatency(ms) {
		this.latencies.push(ms);
		if (this.latencies.length > LATENCY_WINDOW) this.latencies.shift();
		this.updateActivity({ diskLatencyMs: Math.round(this.latencies.reduce((a, b) => a + b, 0) / this.latencies.length) });
	}
	setNetwork(s) { if (this.networkState !== s) { this.networkState = s; this.networks.emit(s); } }
	networkInterface() {
		return {
			loginUrlCb: (url) => { this.networkLoginUrl = url; this.setNetwork("login-ready"); },
			stateUpdateCb: (s) => { if (s === TAILSCALE_RUNNING) this.setNetwork("connected"); },
			netmapUpdateCb: (map) => {
				const addrs = (map && map.self && map.self.addresses) || [];
				const ip = addrs.find((a) => a.includes(".")) || addrs[0] || null;
				if (ip !== this.networkIp) { this.networkIp = ip; this.networks.emit(this.networkState); }
			},
		};
	}
	async loginToNetwork() {
		const cx = this.cx;
		if (!cx || this.stateValue !== "running") throw new LinuxError("NETWORK_LOGIN_FAILED", "Start the machine before connecting it to Tailscale.");
		this.setNetwork("connecting");
		try { await cx.networkLogin(); }
		catch (err) { this.setNetwork("login-failed"); throw new LinuxError("NETWORK_LOGIN_FAILED", "Tailscale login failed (" + (err && err.message ? err.message : String(err)) + ").", err); }
	}
	async writeExportHelper(table) {
		// The DataDevice refuses nested paths, hence /data/export.sh.
		try { await table.data.writeFile(EXPORT_HELPER.slice("/data".length), EXPORT_HELPER_SCRIPT); } catch (e) {}
	}
	// WebVM keeps the shell alive by re-running it after `exit`.
	async runLoop(cx, machine) {
		let failures = 0;
		while (!this.stopped) {
			try {
				await cx.run(machine.cmd, machine.args, { env: machine.env, cwd: machine.cwd, uid: machine.uid, gid: machine.gid });
				if (this.stopped || this.cx !== cx) return;
				failures = 0;
				this.updateActivity({ processes: 0 });
			} catch (err) {
				if (this.stopped || this.cx !== cx) return;
				if (++failures >= 3) {
					this.errorValue = { code: "PROCESS_FAILED", message: machine.cmd + " couldn't be started (" + (err && err.message ? err.message : String(err)) + ")." };
					this.stopped = true;
					this.setState("error");
					return;
				}
			}
			await sleep(100); // a command that exits at once must not become a hot loop
		}
	}
}

// --- reset (fresh document only) ---------------------------------------------------------
// Confirmed reset: record the machine id, reload this tab, and delete its databases at the start
// of the next page load, BEFORE CheerpX is imported, so no vendor worker can write them back.
export function requestReset(id) { writeJSON(RESET_KEY, { id, at: Date.now() }); }
// The machine's lock is taken first: a CheerpX document keeps its machine's lock until it unloads
// (see stop()), so holding it proves no other tab still has that disk open. If another tab has it,
// nothing is deleted (a blocked deleteDatabase would stay queued and erase the disk later, when
// that tab closes) and the marker is cleared: the user retries after stopping it there.
export async function consumePendingReset(del = deleteMachineDisks, acquire = acquireMachineLock) {
	const r = readJSON(RESET_KEY, null);
	if (!r || typeof r.id !== "string" || typeof r.at !== "number") { if (r) remove(RESET_KEY); return null; }
	remove(RESET_KEY);
	if (Date.now() - r.at > 10 * 60 * 1000) return null; // stale marker: never erase later by surprise
	let release;
	try { release = await acquire(r.id); }
	catch (e) { return { id: r.id, ok: false, reason: "open-elsewhere" }; }
	try { return { id: r.id, ok: await del(r.id) }; }
	finally { try { release(); } catch (e) {} }
}
